import {
  formatDateBR,
  formatarCnpjCpf,
  formatHorarioTeoricoLabel,
} from "../utils.js";

/**
 * Constrói a estrutura de seções para o FormReview do TCA Comum.
 *
 * @param {object} params
 * @param {object} params.formData - Dados do formulário TCA
 * @param {boolean} [params.isEstudante=false] - Se o usuário logado é o aprendiz
 * @param {Array} [params.horariosTeoricosDisponiveis=[]] - Opções de horários teóricos
 * @param {object} [params.horario={}] - Configuração de horário (fixo/alternado)
 * @param {string} [params.localAtividade=""] - Identificador do local de atividade (MATRIZ/id filial)
 * @param {object|null} [params.dadosFilialSelecionada=null] - Dados da filial selecionada
 * @returns {Array<{ id: string, section: string, fields: Array<{ label: string, value: any, complete: boolean }> }>}
 */
export const buildReviewDataTca = ({
  formData = {},
  isEstudante = false,
  horariosTeoricosDisponiveis = [],
  horario = {},
  localAtividade = "",
  dadosFilialSelecionada = null,
} = {}) => {
  const sections = [];

  // Quando o(a) Aprendiz acessa, exibir APENAS o bloco "Dados do(a) Aprendiz"
  if (isEstudante) {
    const dadosAprendizFields = [];
    if (formData.cpf_aprendiz?.trim()) {
      dadosAprendizFields.push({ label: "CPF do Aprendiz", value: formData.cpf_aprendiz, complete: true });
    }
    if (formData.nome_aprendiz?.trim()) {
      dadosAprendizFields.push({ label: "Nome Completo", value: formData.nome_aprendiz, complete: true });
    }
    if (formData.telefone_contato?.trim()) {
      dadosAprendizFields.push({ label: "Telefone/Informações adicionais de contato", value: formData.telefone_contato, complete: true });
    }
    if (formData.email_aprendiz?.trim()) {
      dadosAprendizFields.push({ label: "E-mail", value: formData.email_aprendiz, complete: true });
    }
    return [
      {
        id: "dados-aprendiz",
        section: "Dados do(a) Aprendiz",
        fields: dadosAprendizFields,
      },
    ];
  }

  // 1. Dados da Empresa Contratante
  const empresaContratanteFields = [];
  if (formData.razao_social?.trim()) {
    empresaContratanteFields.push({ label: "Razão Social", value: formData.razao_social, complete: true });
  }
  if (formData.cnpj_ou_cpf?.trim()) {
    empresaContratanteFields.push({ label: "CNPJ/CPF", value: formatarCnpjCpf(formData.cnpj_ou_cpf), complete: true });
  }
  if (formData.telefone_empresa_primario?.trim()) {
    empresaContratanteFields.push({ label: "Telefone", value: formData.telefone_empresa_primario, complete: true });
  }
  if (formData.email_empresa?.trim()) {
    empresaContratanteFields.push({ label: "E-mail", value: formData.email_empresa, complete: true });
  }
  if (empresaContratanteFields.length > 0) {
    sections.push({
      id: "dados-empresa-contratante",
      section: "Dados da Empresa Contratante",
      fields: empresaContratanteFields,
    });
  }

  // 2. Representante Legal (Quem irá assinar o contrato)
  const representanteLegalFields = [];
  if (formData.nome_empresa?.trim()) {
    representanteLegalFields.push({ label: "Nome", value: formData.nome_empresa, complete: true });
  }
  if (formData.cargo_empresa?.trim()) {
    representanteLegalFields.push({ label: "Cargo", value: formData.cargo_empresa, complete: true });
  }
  if (formData.telefone_empresa?.trim()) {
    representanteLegalFields.push({ label: "Telefone", value: formData.telefone_empresa, complete: true });
  }
  if (formData.email_empresa_contratante?.trim()) {
    representanteLegalFields.push({ label: "Email", value: formData.email_empresa_contratante, complete: true });
  }
  if (representanteLegalFields.length > 0) {
    sections.push({
      id: "representante-legal",
      section: "Representante Legal (Quem irá assinar o contrato)",
      fields: representanteLegalFields,
    });
  }

  // 3. Dados do(a) Aprendiz
  const dadosAprendizFields = [];
  if (formData.cpf_aprendiz?.trim()) {
    dadosAprendizFields.push({ label: "CPF do Aprendiz", value: formData.cpf_aprendiz, complete: true });
  }
  if (formData.nome_aprendiz?.trim()) {
    dadosAprendizFields.push({ label: "Nome Completo", value: formData.nome_aprendiz, complete: true });
  }
  if (formData.telefone_contato?.trim()) {
    dadosAprendizFields.push({ label: "Telefone/Informações adicionais de contato", value: formData.telefone_contato, complete: true });
  }
  if (formData.email_aprendiz?.trim()) {
    dadosAprendizFields.push({ label: "E-mail", value: formData.email_aprendiz, complete: true });
  }
  if (dadosAprendizFields.length > 0) {
    sections.push({
      id: "dados-aprendiz",
      section: "Dados do(a) Aprendiz",
      fields: dadosAprendizFields,
    });
  }

  // 4. Dados da Aprendizagem
  const dadosAprendizagemFields = [];
  if (formData.data_inicio_contrato?.trim()) {
    dadosAprendizagemFields.push({ label: "Data do início do contrato", value: formatDateBR(formData.data_inicio_contrato), complete: true });
  }
  if (formData.data_fim_contrato?.trim()) {
    dadosAprendizagemFields.push({ label: "Vigência Final", value: formatDateBR(formData.data_fim_contrato), complete: true });
  }
  if (formData.curso?.trim()) {
    dadosAprendizagemFields.push({ label: "Curso", value: formData.curso, complete: true });
  }
  if (formData.carga_horario_curso?.trim()) {
    dadosAprendizagemFields.push({ label: "Carga horária diária", value: formData.carga_horario_curso, complete: true });
  }
  if (formData.salario && formData.salario !== "R$ 0,00" && Number(formData.salario) > 0) {
    const salarioFmt = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(formData.salario) || 0);
    dadosAprendizagemFields.push({ label: "Salário", value: salarioFmt, complete: true });
  }
  if (formData.horario_teorica) {
    const horarioTeorica = horariosTeoricosDisponiveis?.find((h) => String(h.id) === String(formData.horario_teorica));
    const horarioTeoricaLabel = horarioTeorica
      ? formatHorarioTeoricoLabel(horarioTeorica)
      : formData.horario_teorica;
    dadosAprendizagemFields.push({ label: "Horário da aprendizagem teórica", value: horarioTeoricaLabel, complete: true });
  }
  if (typeof horario !== "undefined" && horario?.tipo) {
    const tipoLabel = horario.tipo === "fixo" ? "Horário Fixo" : horario.tipo === "alternado" ? "Horário Alternado" : "";
    if (tipoLabel) {
      dadosAprendizagemFields.push({ label: "Tipo do horário da aprendizagem prática (Fixo ou Alternado)", value: tipoLabel, complete: true });
    }
    if (horario.tipo === "fixo") {
      if (horario.diasSelecionados && horario.diasSelecionados.length > 0) {
        dadosAprendizagemFields.push({ label: "Dias da semana de atuação", value: horario.diasSelecionados.join(", "), complete: true });
      }
      if (horario.fixo?.entrada) {
        dadosAprendizagemFields.push({ label: "Horário de entrada", value: horario.fixo.entrada, complete: true });
      }
      if (horario.fixo?.saida) {
        dadosAprendizagemFields.push({ label: "Horário de saída", value: horario.fixo.saida, complete: true });
      }
    }
    if (horario.tipo === "alternado") {
      const dias = [
        { key: "segunda", label: "Segunda" },
        { key: "terca", label: "Terça" },
        { key: "quarta", label: "Quarta" },
        { key: "quinta", label: "Quinta" },
        { key: "sexta", label: "Sexta" },
      ];
      dias.forEach((dia) => {
        const alt = horario.alternado?.[dia.key];
        if (alt?.entrada && alt?.saida) {
          dadosAprendizagemFields.push({ label: `${dia.label} (Entrada/Saída)`, value: `${alt.entrada} - ${alt.saida}`, complete: true });
        }
      });
    }
  }
  if (formData.endereco_tutor?.trim()) {
    dadosAprendizagemFields.push({ label: "Endereço completo da aprendizagem prática", value: formData.endereco_tutor, complete: true });
  }
  if (formData.endereco_atividade_teorica?.trim()) {
    dadosAprendizagemFields.push({ label: "Endereço da atividade teórica", value: formData.endereco_atividade_teorica, complete: true });
  }
  if (formData.atividades_aprendiz?.trim()) {
    dadosAprendizagemFields.push({ label: "Atividades do aprendiz na empresa", value: formData.atividades_aprendiz, complete: true });
  }
  if (dadosAprendizagemFields.length > 0) {
    sections.push({
      id: "dados-aprendizagem",
      section: "Dados da Aprendizagem",
      fields: dadosAprendizagemFields,
    });
  }

  // 5. Local de Atividade
  let localLabel = "";
  let localComplete = false;
  if (localAtividade === "MATRIZ") {
    localLabel = "Matriz";
    localComplete = true;
  } else if (
    localAtividade &&
    localAtividade !== "FILIAL" &&
    !isNaN(Number(localAtividade))
  ) {
    localLabel = dadosFilialSelecionada?.nome_filial
      ? `Filial: ${dadosFilialSelecionada.nome_filial}`
      : "Filial selecionada";
    localComplete = true;
  } else if (localAtividade === "FILIAL") {
    localLabel = "Filial (selecione a unidade)";
    localComplete = false;
  } else {
    localLabel = "";
    localComplete = false;
  }
  if (localLabel) {
    sections.push({
      id: "local",
      section: "Local de Atividade",
      fields: [
        {
          label: "Local de realização das atividades",
          value: localLabel,
          complete: localComplete,
        },
      ],
    });
  }

  return sections;
};
